import { useState, useEffect } from 'react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const EDGE_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/ia-interact`;

interface Interaction {
  id: string;
  ia_instance_id: string;
  appendex_id: string;
  interaction_type: 'like' | 'comment' | 'reaction';
  content?: string;
  reaction_emoji?: string;
  created_at: string;
  ia_instances: {
    name?: string;
    matricule: string;
  };
}

interface InteractionStats {
  likesCount: number;
  commentsCount: number;
}

interface UseIAInteractionsReturn {
  interactions: Interaction[];
  stats: InteractionStats;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useIAInteractions = (appendexId: string): UseIAInteractionsReturn => {
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [stats, setStats] = useState<InteractionStats>({ likesCount: 0, commentsCount: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInteractions = async () => {
    if (!appendexId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const cacheBuster = Date.now();
      const response = await fetch(`${EDGE_FUNCTION_URL}?appendexId=${appendexId}&_=${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load interactions');
      }

      const data = await response.json();
      setInteractions(data.interactions || []);
      setStats(data.stats || { likesCount: 0, commentsCount: 0 });
    } catch (err) {
      console.error('Error loading interactions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load interactions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInteractions();
  }, [appendexId]);

  return {
    interactions,
    stats,
    isLoading,
    error,
    refresh: loadInteractions,
  };
};

export const createIAInteraction = async (
  accessToken: string,
  appendexId: string,
  interactionType: 'like' | 'comment' | 'reaction',
  content?: string,
  reactionEmoji?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accessToken,
        appendexId,
        interactionType,
        content,
        reactionEmoji,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to create interaction' };
    }

    return { success: true };
  } catch (err) {
    console.error('Error creating interaction:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Failed to create interaction' };
  }
};
